import { reportQueries } from '@/entities/report';
import { useModalStore } from '@/shared/lib/store/model/modal.store';
import { ErrorPopup, Modal } from '@/shared/ui';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useState } from 'react';

interface ReportEditButtonProps {
    myScore: number;
    totalScore: number;
    submAt?: FlagYn;
    targetId: number;
    stntId: string;
    evlIemId: string;
    subId: number;
    type: 'stnt' | 'stnts';
}
export const ReportEditButton: FC<ReportEditButtonProps> = ({
    totalScore,
    submAt,
    targetId,
    stntId,
    evlIemId,
    subId,
    type,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    // 수정 버튼 클릭 시 모달을 오픈합니다.
    const handleEditClick = () => {
        setIsModalOpen(true);
    };

    // ScoreModifyPopup 내부에서 모달 닫힘 호출 시 실행되는 핸들러입니다.
    const handleModalClose = () => {
        setIsModalOpen(false);
    };

    return (
        <>
            <Modal open={isModalOpen} isCustom={true}>
                <ScoreModifyPopup
                    totalScore={totalScore}
                    submAt={submAt}
                    modalClose={handleModalClose}
                    evlId={targetId}
                    userId={stntId}
                    evlIemId={evlIemId}
                    subId={subId}
                    type={type}
                />
            </Modal>
            <button className="edit_bt" onClick={handleEditClick}>
                [수정]
            </button>
        </>
    );
};

interface Props {
    totalScore: number;
    submAt?: FlagYn;
    modalClose: () => void;
    evlId: number;
    userId: string;
    evlIemId: string;
    subId: number;
    type: 'stnt' | 'stnts';
}

const ScoreModifyPopup: FC<Props> = ({
    totalScore: evlIemScr,
    submAt,
    modalClose,
    evlId,
    userId,
    evlIemId,
    subId,
    type,
}) => {
    const queryClient = useQueryClient();
    const [score, setScore] = useState<string>('');
    const [errMsg, setErrMsg] = useState<string>('');
    // 로컬 상태로 확인 모달의 열림/닫힘을 관리합니다.
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const { showModal } = useModalStore();

    const { refetch: stntRefetch } = reportQueries.stnt.getStntSrchEvalResultDetail({
        evlId: evlId,
        stntId: userId,
    });

    const { refetch: stntsListRefetch } = reportQueries.stnts.getReportEvalDetailList({
        evlId: evlId,
    });

    const { refetch: stntsDetailRefetch } = reportQueries.stnts.getReportEvalDetailStnt({
        evlId: evlId,
        evlIemId: evlIemId,
        userId: userId,
        subId: subId,
    });

    // mutation 훅을 통해 점수 수정 API 호출
    const evalModifyScore = reportQueries.stnts.postReportEvalModiScore();

    // onModify: mutation 실행
    const onModify = () => {
        const param = {
            evlId,
            userId,
            evlIemId,
            evlIemScrResult: Number(score),
            subId,
        };
        evalModifyScore.mutate(param, {
            onSuccess: (res) => {
                if (res) {
                    // showToast('점수가 수정되었습니다.');
                    queryClient.invalidateQueries({ queryKey: ['report', 'summary'] });
                    modalClose();
                    refetchHandler();
                } else {
                    alert('수정 실패');
                }
            },
            onError: () =>
                showModal({
                    isCustom: true,
                    id: 'error',
                    initContents: () => <ErrorPopup />,
                }),
        });
    };

    // 리패치 stnt, stnts 분기처리
    const refetchHandler = () => {
        if (type === 'stnt') {
            stntRefetch();
        } else {
            stntsListRefetch();
            stntsDetailRefetch();
        }
    };

    // 점수 형식 검증: 0 이상 최고점 이하, 소숫점 1자리까지 허용
    const validateScore = (numScore: number): boolean => {
        const pattern2 = /^\d+(?:[.]?[\d])?$/; // 소숫점 1자리 체크
        if (evlIemScr && score && (numScore < 0 || numScore > evlIemScr || !pattern2.test(score))) {
            setErrMsg('잘못된 형식입니다');
            return false;
        }
        setErrMsg('');
        return true;
    };

    // 점수 입력 후 제출 처리 함수
    const saveScore = () => {
        const numScore = Number(score);
        if (!validateScore(numScore)) return;

        // submAt가 'N'이면 확인 모달을 띄움
        if (submAt === 'N') {
            setIsConfirmModalOpen(true);
            return;
        }
        // submAt가 'N'이 아닌 경우 바로 mutation 실행
        onModify();
    };

    const changeScore = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.currentTarget;
        // 숫자와 소숫점만 허용
        const filteredScore = value.replace(/[^0-9.]/g, '');
        setScore(filteredScore);
    };

    // 확인 모달에서 "예" 클릭 시 mutation 실행 후 모달 닫기
    const handleConfirm = () => {
        setIsConfirmModalOpen(false);
        onModify();
    };

    // 확인 모달에서 "아니요" 클릭 시 모달 닫기
    const handleCancel = () => {
        setIsConfirmModalOpen(false);
    };

    return (
        <>
            {/* 확인 모달: submAt가 'N'인 경우에만 열림 */}
            {/* <Modal open={isConfirmModalOpen} isCustom={true}>
                <div className="ki-modal" style={{ display: 'block' }}>
                    <div className="modal-footer">
                        <button onClick={handleCancel}>아니요</button>
                        <button className="ok" onClick={handleConfirm}>
                            예
                        </button>
                    </div>
                    <div className="modal-body">
                        <p>미제출 학생의 점수를 수정하시겠습니까?</p>
                        <p>점수 수정 시, 제출 학생으로 변경됩니다.</p>
                        <br />
                        <p>
                            (수정하지 않은 문항들은 모두 자동 오답(X) 처리되며, 추가 수정을 통해
                            정답(O)으로 바꿀 수 있습니다.)
                        </p>
                    </div>
                </div>
            </Modal> */}

            <div className="pop_wrap box-width4">
                <div className="modal_header">
                    <div className="title">평가 점수 수정</div>
                </div>
                <div className="pop_contents cont-pd4">
                    <div className="pop-txt-style5">0~최고점 사이의 값을 입력해주세요</div>
                    <div className="pop-txt-style7">(최고점: {evlIemScr})</div>
                    <div className="score-input">
                        <input
                            type="text"
                            id="edit_txt"
                            className="input-txt input-style3"
                            aria-label="숫자를 입력하세요"
                            placeholder="숫자를 입력하세요"
                            value={score}
                            onChange={changeScore}
                        />
                    </div>
                    <div className="error_txt">{errMsg}</div>
                    <div className="txt" style={{ display: 'none' }}>
                        학생에게 수정된 점수를 보여주려면
                        <br />
                        반드시 목록 화면에서 <button className="edit_bt">[수정반영]</button> 버튼을
                        눌러 주세요.
                    </div>
                    <div className="btn-wrap // con-mt3">
                        <button className="btn btn-default blue" onClick={saveScore}>
                            완료
                        </button>
                    </div>
                </div>
                <button type="button" className="btn-x btn-x-black" onClick={modalClose}>
                    <span className="sr-only">팝업 닫기</span>
                </button>
            </div>
        </>
    );
};

export default ScoreModifyPopup;
